import { Data, List, NeoModel, NotifyUtils, Validation } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import ManageFileSpecVM from '../Components/ManageFileSpecVM';
import ManageExportFileSpecComponentVM from '../Components/ManageExportFileSpecVM';
import CreateFileSpecCommand from '../Models/Commands/CreateFileSpecCommand';
import FileSpecLookup from '../Models/Lookups/FileSpecLookup';
import FileTypeLookup from '../Models/Lookups/FileTypeLookup';
import FileTypeSearchCriteria from '../Models/Queries/FileTypeSearchCriteria';
import FileTypeConfigurationLookup from '../Models/Lookups/FileTypeConfigurationLookup';
import UpdateFileSpecCommand from '../Models/Commands/UpdateFileSpecCommand';
import GetFileSpecDateOverlapQuery from '../Models/Queries/GetFileSpecDateOverlapQuery';
import FileGroupingFileSpecView from '../../FileGroupingFileSpecs/Views/FileGroupingFileSpecView';
import ColumnConfigurationsView from '../../ColumnConfigurations/Views/ColumnConfigurationsView';
import StaticExportFileSpecPartialLookup from '../Models/Lookups/StaticExportFileSpecPartialLookup';
import StaticExportFileSpecLookup from '../Models/Lookups/StaticExportFileSpecLookup';
import FileGroupingFileSpecExportConfigurationComponentVM from '../../ExportFileGroupingFileSpecs/Components/FileGroupingFileSpecExportConfigurationComponentVM';
import UpdateExportFileSpecCommand from '../Models/Commands/UpdateExportFileSpecCommand';
import CreateExportFileSpecCommand from '../Models/Commands/CreateExportFileSpecCommand';
import ExportColumnConfigurationsView from '../../ExportColumnSpecs/Views/ExportColumnConfigurationsView';
import ManageFileGroupingFileSpecExportConfigurationComponentVM from '../../ExportFileGroupingFileSpecs/Components/ManageFileGroupingFileSpecExportConfigurationComponentVM';
import FileGroupingExportFileSpecLookup from '../../ExportFileGroupingFileSpecs/Models/Lookups/FileGroupingExportFileSpecLookup';

@NeoModel
export default class FileSpecConfigurationVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public fileSpecApiClient = AppService.get(Types.ApiClients.FileSpecApiClient),
        public exportFileSpecApiClient = AppService.get(Types.ApiClients.StaticExportFileSpecApiClient),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {
        super(taskRunner);
    }

    //// Import File Configuration
    public importFileTypeSearchCriteria = new FileTypeSearchCriteria();
    public manageFileSpecComponentVM = new ManageFileSpecVM();
    public getFileSpecDateOverlapQuery = new GetFileSpecDateOverlapQuery();
    public addNewFileSpecInd: boolean = false;
    public updateExistingFileSpecInd: boolean = false;
    public addFileSpecFromExistingVersionInd: boolean = false;
    public fileSpecDatesOverlapWithAnotherVersionInd: boolean = false;
    public previousVersionOfFileSpec = new FileSpecLookup();

    //// Export file configuration
    public exportFileTypeSearchCriteria = new FileTypeSearchCriteria();
    public manageExportFileSpecComponentVM = new ManageExportFileSpecComponentVM();
    public addNewExportFileSpecInd: boolean = false;
    public updateExistingExportFileSpecInd: boolean = false;
    public previousVersionOfExportFileSpec = new StaticExportFileSpecLookup();

    //// File grouping export file spec
    public staticExportFileSpecList = new List(StaticExportFileSpecLookup);
    public fileGroupingExportFileSpecComponentVM = new FileGroupingFileSpecExportConfigurationComponentVM();

    //// Manage file grouping export file spec
    public manageFileGroupingFileSpecExportConfigurationComponentVM = new ManageFileGroupingFileSpecExportConfigurationComponentVM();
    public showManageFileGroupingExportFileSpecComponent = false;

    public importFileSpecsPageManager = new Data.PageManager(this.importFileTypeSearchCriteria, FileTypeLookup,
        this.fileSpecApiClient.getFileSpecConfigurationList, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: true,
        sortBy: "fileType",
        initialTaskRunner: this.taskRunner,
        afterFetch: this.setImportIsExpandedProp.bind(this),
    });

    public exportFileSpecsPageManager = new Data.PageManager(this.exportFileTypeSearchCriteria, StaticExportFileSpecPartialLookup,
        this.exportFileSpecApiClient.getExportFileSpecPagedList, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: true,
        sortBy: "reportSpecName",
        initialTaskRunner: this.taskRunner,
    });

    public setImportIsExpandedProp(data: FileTypeLookup[]) {
        data.forEach(fileType => {
            if (fileType.fileTypeConfigurationsList.length > 0) {
                fileType.isExpanded = false;
            }
        })
    }

    public async initialise() {
        this.setViewIndicatorsToFalse();
        this.setStaticExportFileSpecList();
        reaction(() => this.importFileTypeSearchCriteria.searchCriteria, () => this.importFileSpecsPageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.exportFileTypeSearchCriteria.searchCriteria, () => this.exportFileSpecsPageManager.refreshData(), { fireImmediately: false, delay: 650 });
    }

    public cancelManageFileGroupingExportFileSpecComponent() {
        this.fileGroupingExportFileSpecComponentVM.showFileGroupingExportFileSpecComponent = false;
        this.fileGroupingExportFileSpecComponentVM.managedFileGroupingExportFileSpec = new FileGroupingExportFileSpecLookup();
        this.fileGroupingExportFileSpecComponentVM.isFieldReadOnlyInd = false;
    }

    public async setStaticExportFileSpecList() {
        var response = await this.exportFileSpecApiClient.getExportFileSpecDropdownList(" ");
        this.staticExportFileSpecList.set(response.data);
    }

    public async addNewFileSpec(fileType?: string, description?: string, fileExtension?: string, fileDelimiter?: string, staticFileSpecID?: number, latestFileSpecVersionID?: number) {
        this.manageFileSpecComponentVM.showManageFileSpecComponent = true;
        this.manageFileSpecComponentVM.validationModeDisplay = Validation.DisplayMode.AfterSubmit;

        if (fileType !== undefined) {
            // Created from an existing version
            this.manageFileSpecComponentVM.isFieldReadOnlyInd = true;
            this.manageFileSpecComponentVM.fileSpecLookupModel.fileType = String(fileType);
            this.manageFileSpecComponentVM.fileSpecLookupModel.description = String(description);
            this.manageFileSpecComponentVM.fileSpecLookupModel.enumFileExtension = String(fileExtension);
            this.manageFileSpecComponentVM.fileSpecLookupModel.enumFileDelimiter = String(fileDelimiter);
            this.manageFileSpecComponentVM.fileSpecLookupModel.staticFileSpecId = Number(staticFileSpecID);
            this.manageFileSpecComponentVM.fileSpecLookupModel.latestFileSpecVersionID = Number(latestFileSpecVersionID);
            this.manageFileSpecComponentVM.fileSpecLookupModel.isCreatedFromPreviousVersionInd = true;
        } else {
            // Create a brand new filespec, not from an existing version
            this.manageFileSpecComponentVM.isFieldReadOnlyInd = false;
            this.manageFileSpecComponentVM.fileSpecLookupModel = new FileSpecLookup();
            this.manageFileSpecComponentVM.fileSpecLookupModel.isCreatedFromPreviousVersionInd = false;
            this.manageFileSpecComponentVM.fileSpecLookupModel.latestFileSpecVersionID = 0;
        }

        this.importFileSpecsPageManager.refreshData();
    }

    public addNewExportFileSpec(reportName?: string, reportReference?: string, format?: string, latestExportFileSpecVersionId?: number) {
        this.manageExportFileSpecComponentVM.showManageExportFileSpecComponent = true;
        this.manageExportFileSpecComponentVM.validationModeDisplay = Validation.DisplayMode.AfterSubmit;

        if (reportName !== undefined) {
            // Created from an old version
            this.manageExportFileSpecComponentVM.isFieldReadonlyInd = true;
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.reportSpecName = String(reportName);
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.reportProcedureName = String(reportReference);
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.format = String(format);
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.latestVersionOfExportFileSpecId = Number(latestExportFileSpecVersionId);
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.isCreatedFromPreviousVersion = true;
        } else {
            // New export file spec, not from an old version
            this.manageExportFileSpecComponentVM.isFieldReadonlyInd = false;
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel = new StaticExportFileSpecLookup();
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.latestVersionOfExportFileSpecId = 0;
            this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.isCreatedFromPreviousVersion = false;
        }
    }

    public setViewIndicatorsToFalse() {
        this.manageFileSpecComponentVM.showManageFileSpecComponent = false;
        this.addNewFileSpecInd = false;
        this.updateExistingFileSpecInd = false;
        this.addFileSpecFromExistingVersionInd = false;
        this.manageFileSpecComponentVM.isFieldReadOnlyInd = false;
        this.manageFileSpecComponentVM.updateFileSpecReadOnlyInd = false;
        this.manageFileSpecComponentVM.activeEndDateIsNull = false;
        this.manageFileSpecComponentVM.showConfirmSaveComponent = false;
        this.manageFileSpecComponentVM.confirmSaveInd = false;

        this.manageExportFileSpecComponentVM.showManageExportFileSpecComponent = false;
        this.addNewExportFileSpecInd = false;
        this.updateExistingExportFileSpecInd = false;
        this.manageExportFileSpecComponentVM.isFieldReadonlyInd = false;
        this.manageExportFileSpecComponentVM.updateExportFileSpecReadOnlyInd = false;
        this.manageExportFileSpecComponentVM.activeEndDateIsNull = false;
        this.manageExportFileSpecComponentVM.showConfirmSaveComponent = false;
        this.manageExportFileSpecComponentVM.confirmSaveInd = false;
    }

    public async doesFileSpecOverlapWithExistingFileSpec() {
        this.mapFileSpecDateOverlapQuery();
        var response = await this.fileSpecApiClient.getFileSpecDateOverlapBool(this.getFileSpecDateOverlapQuery);
        this.fileSpecDatesOverlapWithAnotherVersionInd = (Boolean)(response.data);
        if (this.fileSpecDatesOverlapWithAnotherVersionInd) {
            this.manageFileSpecComponentVM.showConfirmSaveModal();
            this.manageFileSpecComponentVM.confirmSaveInd = false;
        }
        else {
            this.manageFileSpecComponentVM.confirmSaveInd = true;
        }
    }

    public cancel() {
        this.setViewIndicatorsToFalse();
        this.manageFileSpecComponentVM.fileSpecLookupModel = new FileSpecLookup();
        this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel = new StaticExportFileSpecLookup();
    }

    public async clearSearch() {
        this.importFileTypeSearchCriteria.meta.searchCriteria.value = "";
        await this.importFileSpecsPageManager.refreshData();
    }

    public async clearExportSearch() {
        this.exportFileTypeSearchCriteria.meta.searchCriteria.value = "";
        await this.exportFileSpecsPageManager.refreshData();
    }

    public async editFileSpec(fileType: string, fileDescription: string, fileExtension: string, fileDelimiter: string, config: FileTypeConfigurationLookup, latestFileSpecVersionID: number) {
        this.manageFileSpecComponentVM.validationModeDisplay = Validation.DisplayMode.AfterSubmit;
        this.manageFileSpecComponentVM.showManageFileSpecComponent = true;
        this.manageFileSpecComponentVM.fileSpecLookupModel.fileType = fileType;
        this.manageFileSpecComponentVM.fileSpecLookupModel.description = fileDescription;
        this.manageFileSpecComponentVM.fileSpecLookupModel.enumFileExtension = fileExtension;
        this.manageFileSpecComponentVM.fileSpecLookupModel.enumFileDelimiter = fileDelimiter;
        this.manageFileSpecComponentVM.fileSpecLookupModel.activeEndDate = config.activeEndDate;
        this.manageFileSpecComponentVM.fileSpecLookupModel.activeStartDate = config.activeStartDate;
        this.manageFileSpecComponentVM.fileSpecLookupModel.rawDataTableName = config.rawDataTableName;
        this.manageFileSpecComponentVM.fileSpecLookupModel.errorHoldingTableName = config.errorHoldingTableName;
        this.manageFileSpecComponentVM.fileSpecLookupModel.rawGenericTableName = config.rawGenericTableName;
        this.manageFileSpecComponentVM.fileSpecLookupModel.columnCount = config.columnCount;
        this.manageFileSpecComponentVM.fileSpecLookupModel.hasCheckConstraintInd = config.hasCheckConstraintInd;
        this.manageFileSpecComponentVM.fileSpecLookupModel.startColumnHeaderRead = config.startColumnHeaderRead;
        this.manageFileSpecComponentVM.fileSpecLookupModel.containHeaderInd = config.containHeaderInd;
        this.manageFileSpecComponentVM.fileSpecLookupModel.isSingleFileInd = config.isSingleFileInd;
        this.manageFileSpecComponentVM.fileSpecLookupModel.isIgnoredInd = config.isIgnoredInd;
        this.manageFileSpecComponentVM.fileSpecLookupModel.latestFileSpecVersionID = latestFileSpecVersionID;
        this.manageFileSpecComponentVM.fileSpecLookupModel.staticFileSpecId = config.staticFileSpecId;
        this.manageFileSpecComponentVM.isFieldReadOnlyInd = true;
        this.manageFileSpecComponentVM.updateFileSpecReadOnlyInd = true;
        this.importFileSpecsPageManager.refreshData();
    }

    public async editExportFileSpec(staticExportFileSpecId: number) {
        var response = await this.exportFileSpecApiClient.getExportFileSpecById(staticExportFileSpecId);
        this.manageExportFileSpecComponentVM.validationModeDisplay = Validation.DisplayMode.AfterSubmit;
        this.manageExportFileSpecComponentVM.showManageExportFileSpecComponent = true;
        this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.mapFrom(response.data);
        this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.isActiveInd = response.data.isActiveInd;
        this.manageExportFileSpecComponentVM.isFieldReadonlyInd = true;
        this.manageExportFileSpecComponentVM.updateExportFileSpecReadOnlyInd = true;
        this.exportFileSpecsPageManager.refreshData();
    }

    public async updateActiveEndDateOfPreviousFileSpecVersion(staticFileSpecID: number, activeStartDateOfNewVersion: Date) {
        var response = await this.fileSpecApiClient.getFileSpecForFileSpecID(staticFileSpecID);
        this.previousVersionOfFileSpec = response.data;

        var activeEndDateToUpdate = new Date(activeStartDateOfNewVersion);
        activeEndDateToUpdate.setDate(activeEndDateToUpdate.getDate() - 1);

        this.previousVersionOfFileSpec.activeEndDate = activeEndDateToUpdate;
        await this.fileSpecApiClient.updateFileSpec((this.mapFileSpecToUpdateCommand(this.previousVersionOfFileSpec)!.toJSObject()));
    }

    public viewBrokersForFileSpec(staticFileSpecID?: number) {
        this.navigation.navigateToView(FileGroupingFileSpecView, { fileGroupingID: String(null), broker: String(null), fileSpecID: String(staticFileSpecID), backURL: this.navigation.getCurrentViewPath() })
    }

    public viewColumnsForFileSpec(staticFileSpecID: number, fileSpecProps: string) {
        this.navigation.navigateToView(ColumnConfigurationsView, { fileSpecID: String(staticFileSpecID), fileSpecProps: String(fileSpecProps) })
    }

    public viewBrokersForExportFileSpec(staticExportFileSpecId: number, fileSpecPropertiesConcatenated: string) {
        this.fileGroupingExportFileSpecComponentVM.initialise();
        this.fileGroupingExportFileSpecComponentVM.staticExportFileSpecId = staticExportFileSpecId;
        this.fileGroupingExportFileSpecComponentVM.fileGroupingExportFileSpecSearchCriteria.meta.staticExportFileSpecId.value = staticExportFileSpecId;
        this.fileGroupingExportFileSpecComponentVM.fileGroupingExportFileSpecSearchCriteria.meta.fileTypeConcatenated.value = fileSpecPropertiesConcatenated;
        this.fileGroupingExportFileSpecComponentVM.pageManager.refreshData();
        this.fileGroupingExportFileSpecComponentVM.showFileGroupingExportFileSpecComponent = true;
    }

    public viewColumnsForExportFileSpec(staticFileSpecID: number, fileSpecProps: string) {
        this.navigation.navigateToView(ExportColumnConfigurationsView, { exportFileSpecId: String(staticFileSpecID), exportFileSpecProps: String(fileSpecProps) })
    }

    public async saveFileSpec() {
        this.taskRunner.run(async () => {
            if (!this.manageFileSpecComponentVM.confirmSaveInd) {
                await this.doesFileSpecOverlapWithExistingFileSpec();
            }
            else {
                var response = this.manageFileSpecComponentVM.fileSpecLookupModel.staticFileSpecId > 0
                    ? await this.fileSpecApiClient.updateFileSpec((this.mapFileSpecToUpdateCommand(this.manageFileSpecComponentVM.fileSpecLookupModel)!.toJSObject()))
                    : await this.fileSpecApiClient.addNewFileSpec((this.mapFileSpecToCreateCommand()!.toJSObject()));

                if (this.manageFileSpecComponentVM.fileSpecLookupModel.isCreatedFromPreviousVersionInd
                    && this.manageFileSpecComponentVM.fileSpecLookupModel.latestFileSpecVersionID != null
                    && this.manageFileSpecComponentVM.fileSpecLookupModel.activeStartDate != null) {
                    await this.updateActiveEndDateOfPreviousFileSpecVersion(
                        this.manageFileSpecComponentVM.fileSpecLookupModel.latestFileSpecVersionID,
                        this.manageFileSpecComponentVM.fileSpecLookupModel.activeStartDate);
                }

                if (response.data.success) {
                    await this.importFileSpecsPageManager.refreshData();
                    NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FileSpecSaved, NotificationDuration.Standard);
                }
                else {
                    NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                }
                this.manageFileSpecComponentVM.fileSpecLookupModel = new FileSpecLookup();
                this.setViewIndicatorsToFalse();
                this.importFileSpecsPageManager.refreshData();
            }
        })
    }

    public async saveExportFileSpec() {
        this.taskRunner.run(async () => {
            if (this.manageExportFileSpecComponentVM.confirmSaveInd) {
                var response = this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel.staticExportFileSpecId > 0
                    ? await this.exportFileSpecApiClient.updateExportFileSpec((this.mapExportFileSpecToUpdateCommand(this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel)!.toJSObject()))
                    : await this.exportFileSpecApiClient.addNewExportFileSpec((this.mapExportFileSpecToCreateCommand()!.toJSObject()));

                if (response.data.success) {
                    await this.exportFileSpecsPageManager.refreshData();
                    NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.FileSpecSaved, NotificationDuration.Standard);
                }
                else {
                    NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                }
                this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel = new StaticExportFileSpecLookup();
                this.setViewIndicatorsToFalse();
                this.exportFileSpecsPageManager.refreshData();
            }
        })
    }

    private mapFileSpecToCreateCommand() {
        var command = new CreateFileSpecCommand().mapFrom(this.manageFileSpecComponentVM.fileSpecLookupModel);
        return command;
    }

    private mapFileSpecToUpdateCommand(fileSpecLookup: FileSpecLookup) {
        var command = new UpdateFileSpecCommand().mapFrom(fileSpecLookup);
        return command;
    }

    private mapExportFileSpecToCreateCommand() {
        var command = new CreateExportFileSpecCommand().mapFrom(this.manageExportFileSpecComponentVM.managedExportFileSpecLookupModel);
        return command;
    }

    private mapExportFileSpecToUpdateCommand(fileSpecLookup: StaticExportFileSpecLookup) {
        var command = new UpdateExportFileSpecCommand().mapFrom(fileSpecLookup);
        return command;
    }

    private mapFileSpecDateOverlapQuery() {
        this.getFileSpecDateOverlapQuery.activeStartDate = this.manageFileSpecComponentVM.fileSpecLookupModel.activeStartDate;
        this.getFileSpecDateOverlapQuery.fileDelimiter = this.manageFileSpecComponentVM.fileSpecLookupModel.enumFileDelimiter;
        this.getFileSpecDateOverlapQuery.fileExtension = this.manageFileSpecComponentVM.fileSpecLookupModel.enumFileExtension;
        this.getFileSpecDateOverlapQuery.staticFileSpecID = this.manageFileSpecComponentVM.fileSpecLookupModel.staticFileSpecId;
        this.getFileSpecDateOverlapQuery.fileType = this.manageFileSpecComponentVM.fileSpecLookupModel.fileType;
    }
}